// NATS Subject Patterns
export const NATS_SUBJECTS = {
  // Telemetry: tenant.{tenant_id}.telemetry.device.{device_id}.{signal}
  telemetry: (tenantId: string, deviceId: string, signal: string) =>
    `tenant.${tenantId}.telemetry.device.${deviceId}.${signal}`,

  // Alerts: tenant.{tenant_id}.alerts.{type}
  alert: (tenantId: string, alertType: string) => `tenant.${tenantId}.alerts.${alertType}`,

  // Commands: tenant.{tenant_id}.commands.{module}.{action}
  command: (tenantId: string, module: string, action: string) =>
    `tenant.${tenantId}.commands.${module}.${action}`,

  // Events: tenant.{tenant_id}.events.{module}.{event}
  event: (tenantId: string, module: string, event: string) =>
    `tenant.${tenantId}.events.${module}.${event}`,

  // System: system.{component}.{event}
  system: (component: string, event: string) => `system.${component}.${event}`,
} as const;

// NATS Subscription Patterns
export const NATS_SUBSCRIPTIONS = {
  // All telemetry for a tenant
  allTenantTelemetry: (tenantId: string) => `tenant.${tenantId}.telemetry.>`,

  // All telemetry from all tenants (for super admin)
  allTelemetry: () => 'tenant.*.telemetry.>',

  // All alerts for a tenant
  allTenantAlerts: (tenantId: string) => `tenant.${tenantId}.alerts.>`,

  // All events for a tenant
  allTenantEvents: (tenantId: string) => `tenant.${tenantId}.events.>`,

  // All system events
  allSystemEvents: () => 'system.>',

  // Device-specific telemetry
  deviceTelemetry: (tenantId: string, deviceId: string) =>
    `tenant.${tenantId}.telemetry.device.${deviceId}.>`,

  // All Nexus AI Assistant events for a tenant
  allTenantNexusAssistant: (tenantId: string) => `tenant.${tenantId}.events.openclaw.>`,

  // All Nexus AI Assistant events (super admin)
  allNexusAssistant: () => 'tenant.*.events.openclaw.>',
} as const;

// MQTT Topic Patterns (for IoT devices)
export const MQTT_TOPICS = {
  // ns/{org_slug}/{location_slug}/{area}/{device_id}/{signal}
  telemetry: (
    orgSlug: string,
    locationSlug: string,
    area: string,
    deviceId: string,
    signal: string
  ) => `ns/${orgSlug}/${locationSlug}/${area}/${deviceId}/${signal}`,

  // Subscribe to all org topics
  allOrg: (orgSlug: string) => `ns/${orgSlug}/#`,

  // Subscribe to all neurosense topics
  allNeurosense: () => 'ns/#',
} as const;

// OpenClaw system-level events (not tenant-scoped)
export const OPENCLAW_SYSTEM_EVENTS = {
  updateAvailable: () => NATS_SUBJECTS.system('openclaw', 'update_available'),
  updateInstalled: () => NATS_SUBJECTS.system('openclaw', 'update_installed'),
} as const;

// Parse MQTT topic to extract components
export function parseMqttTopic(topic: string): {
  orgSlug: string;
  locationSlug: string;
  area: string;
  deviceId: string;
  signal: string;
} | null {
  const parts = topic.split('/');
  if (parts.length !== 6 || parts[0] !== 'ns') {
    return null;
  }

  const [, orgSlug, locationSlug, area, deviceId, signal] = parts;
  return { orgSlug, locationSlug, area, deviceId, signal };
}

// Convert MQTT topic to NATS subject
export function mqttToNatsSubject(topic: string, tenantId: string): string | null {
  const parsed = parseMqttTopic(topic);
  if (!parsed) return null;

  return NATS_SUBJECTS.telemetry(tenantId, parsed.deviceId, parsed.signal);
}

// =============================================================================
// MODULE-SPECIFIC EVENTS
// =============================================================================

/**
 * LexShift Module Events
 * HR/Scheduling module for employee management
 */
export const LEXSHIFT_EVENTS = {
  // Employee clock events (published by kiosk/mobile)
  employeeClockedIn: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'employee_clocked_in'),
  employeeClockedOut: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'employee_clocked_out'),
  breakStarted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'lexshift', 'break_started'),
  breakEnded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'lexshift', 'break_ended'),

  // Schedule events
  scheduleUpdated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'schedule_updated'),
  schedulePublished: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'schedule_published'),

  // Leave events
  //
  // NAZWA KANONICZNA: `leave_requested` (czas przeszły, jak każde inne zdarzenie
  // w tym słowniku — zdarzenie opisuje FAKT, który już zaszedł). Konsumenci
  // kluczujący na `leave_request` nigdy nie dostaną tego komunikatu: mapa
  // inwalidacji w apps/module-lexshift trzymała właśnie taki klucz, więc lista
  // wniosków u pozostałych zalogowanych nie odświeżała się po złożeniu wniosku.
  leaveRequested: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'leave_requested'),
  leaveApproved: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'lexshift', 'leave_approved'),
  leaveRejected: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'lexshift', 'leave_rejected'),
  // Emitted by the autonomous LexShiftEngine when a leave request has sat
  // `pending` past its review SLA — surfaces the stale request to managers.
  leaveSlaBreached: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'leave_sla_breached'),

  // NEXUS review skills (Faza 5 arming). Published by nexusLexshiftReviews once
  // a PIP labour-law audit / demand forecast completes so the result enters
  // organism memory (Encoder) and the reflex path.
  pipReviewCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'pip_review_completed'),
  demandForecastCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'demand_forecast_completed'),

  // Dozór wysyłki dokumentów kadrowych (KadrySync, etap C-2 planu kadrowego):
  // po 5. dniu miesiąca rejestr nadal nie ma zestawienia ze statusem CREATED
  // za miesiąc poprzedni. Konsument: pocztaZdarzen (e-mail do właściciela).
  // Bez tego zdarzenia BRAK ewidencji jest niewidoczny z definicji — nikt nie
  // dostaje pustego maila o dokumentach, których nie ma.
  ewidencjaNiewyslana: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'lexshift', 'ewidencja_niewyslana'),
} as const;

/**
 * WaterAndIce Module Events
 * Temperature logging for bakeries
 */
export const WATERANDICE_EVENTS = {
  // Temperature events
  temperatureRecorded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'waterandice', 'temperature_recorded'),
  temperatureAlert: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'waterandice', 'temperature_alert'),

  // Dough events
  doughStarted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'waterandice', 'dough_started'),
  doughStatusChanged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'waterandice', 'dough_status_changed'),
  doughReady: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'waterandice', 'dough_ready'),
  // Mixing countdown elapsed (emitted by the timed-tasks automat when a dough's
  // mixing timer runs out — the "hard process gate" signal for HACCP measurement).
  doughTimerDue: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'waterandice', 'dough_timer_due'),
} as const;

/**
 * Module Timed Tasks — generic, module-agnostic contract for the platform-wide
 * timed-task organ (see docs/02-ARCHITECTURE/MODULE_TIMED_TASKS_ORGAN.md). Any module
 * that registers a row in `public.module_timed_tasks` gets these events emitted by the
 * nexus-core automat: `timed_task_started` when it begins, `timed_task_due` when its
 * deadline passes. The `{module}` token keeps them on the same `tenant.*.events.>`
 * stream every consumer (encoder, agenda materializer) already listens to.
 */
export const TIMED_TASK_EVENTS = {
  started: (tenantId: string, module: string) =>
    NATS_SUBJECTS.event(tenantId, module, 'timed_task_started'),
  due: (tenantId: string, module: string) =>
    NATS_SUBJECTS.event(tenantId, module, 'timed_task_due'),
} as const;

export const TIMED_TASK_SUBSCRIPTIONS = {
  // Any module's timed-task lifecycle, across all tenants (super-admin / automat).
  allStarted: () => 'tenant.*.events.*.timed_task_started',
  allDue: () => 'tenant.*.events.*.timed_task_due',
} as const;

/**
 * Flat payload mirrored into the timed-task envelopes; the automat's
 * `agenda.task_created` carries the same field names. Since 26.10.2026 that
 * message is an Envelope v2 too — the Agenda materializer reads it through
 * `readEventPayload`, so the field names are the contract, not the nesting.
 */
export interface TimedTaskPayload {
  tenant_id: string;
  /** Producing module, e.g. 'waterandice'. */
  source: string;
  /** Idempotency key, e.g. 'waterandice/mixing/<doughId>'. */
  source_reference: string;
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  /** ISO-8601 deadline; presence is what makes Agenda create a dated assignment. */
  due_at?: string;
  /** Discriminator for the hard-gate soul reaction, e.g. 'timed_task'. */
  kind?: string;
  module?: string;
  /** Resolved worker (often null for anonymous producers → Nexus router decides). */
  assignee_worker_id?: string;
}

/**
 * NeuroSense Module Events
 * IoT sensor monitoring
 */
export const NEUROSENSE_EVENTS = {
  // Sensor events
  sensorReading: (tenantId: string, deviceId: string) =>
    NATS_SUBJECTS.telemetry(tenantId, deviceId, 'reading'),
  sensorAlert: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'neurosense', 'sensor_alert'),
  sensorOffline: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'neurosense', 'sensor_offline'),

  // Baseline organ topology (Część G.2 "Zmysły"/"Pamięć"): emitted by
  // neurosense-core when a device is first seen (sensor_registered) and on a
  // throttled liveness reconcile (device_online). These carry device/area/
  // equipment entities so the generic encoder materializes the NeuroSense
  // layer of the Living Network from NORMAL operation — not only on a breach.
  // Semantic + modest salience; dedup-safe via a stable per-day trace_id.
  sensorRegistered: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'neurosense', 'sensor_registered'),
  deviceOnline: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'neurosense', 'device_online'),

  // Threshold breach events (emitted by alert services when HACCP equipment
  // readings cross configured thresholds — consumed by novu-bridge for
  // email/push/in-app delivery).
  temperatureThresholdBreach: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'neurosense', 'temperature_threshold_breach'),
  phThresholdBreach: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'neurosense', 'ph_threshold_breach'),
  // Wilgotność poza zakresem — plan 65 §9.2 (luka L5). Producent:
  // `humidityAlertService.ts`; konsumenci: poczta zdarzeń, graf organizmu.
  humidityThresholdBreach: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'neurosense', 'humidity_threshold_breach'),
  batteryLow: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'neurosense', 'battery_low'),
  // Alarm bezpieczeństwa (zalanie, dym, gaz) — plan 65, etap N (luka L4).
  // Producent: `alarmBezpieczenstwaService.ts`; konsument: poczta zdarzeń.
  safetyAlarm: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'neurosense', 'alarm_bezpieczenstwa'),

  // Area events
  areaOccupancyChanged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'neurosense', 'area_occupancy_changed'),
} as const;

/**
 * Gamification Module Events
 * Worker achievements and points tracking
 */
export const GAMIFICATION_EVENTS = {
  // Achievement events
  achievementEarned: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'gamification', 'achievement_earned'),
  achievementProgress: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'gamification', 'achievement_progress'),

  // Level events
  levelUp: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'gamification', 'level_up'),

  // Points events
  pointsAwarded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'gamification', 'points_awarded'),

  // Leaderboard events
  leaderboardUpdated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'gamification', 'leaderboard_updated'),
} as const;

/**
 * Agenda Module Events
 * Task management with teams, scheduling, and execution tracking
 */
export const AGENDA_EVENTS = {
  // Task events
  taskCreated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'agenda', 'task_created'),
  taskUpdated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'agenda', 'task_updated'),
  taskDeleted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'agenda', 'task_deleted'),

  // Assignment events
  taskAssigned: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'agenda', 'task_assigned'),
  taskStarted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'agenda', 'task_started'),
  taskCompleted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'agenda', 'task_completed'),
  taskOverdue: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'agenda', 'task_overdue'),

  // Schedule events
  scheduleCreated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'agenda', 'schedule_created'),
  scheduleTriggered: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'agenda', 'schedule_triggered'),

  // Group events
  groupCreated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'agenda', 'group_created'),
  groupMemberAdded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'agenda', 'group_member_added'),
  groupMemberRemoved: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'agenda', 'group_member_removed'),
} as const;

/**
 * Provisio Module Events
 * Procurement, MRP, and inventory management
 */
export const PROVISIO_EVENTS = {
  // Inventory events
  inventoryCreated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'inventory_created'),
  inventoryUpdated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'inventory_updated'),
  lowStockAlert: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'provisio', 'low_stock_alert'),
  outOfStockAlert: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'out_of_stock_alert'),

  // Order events
  orderCreated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'provisio', 'order_created'),
  orderSent: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'provisio', 'order_sent'),
  orderConfirmed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'order_confirmed'),
  orderDelivered: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'order_delivered'),
  orderCancelled: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'order_cancelled'),

  // Invoice events
  invoiceUploaded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'invoice_uploaded'),
  invoiceProcessed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'invoice_processed'),
  invoicePaid: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'provisio', 'invoice_paid'),

  // MRP events
  mrpProposalGenerated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'mrp_proposal_generated'),
  mrpOrderCreated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'provisio', 'mrp_order_created'),

  // Stock movement events
  stockReceived: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'provisio', 'stock_received'),
  stockAdjusted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'provisio', 'stock_adjusted'),

  // Partie surowca (wątek W12): przyjęcie partii z atestem — początek śladu
  // „która partia mąki weszła w ten chleb" (provisio_batches z migracji 394).
  batchReceived: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'provisio', 'batch_received'),
} as const;

/**
 * Filtry subskrypcji Provisio na współdzielonym strumieniu EVENTS.
 *
 * Provisio jest aplikacją Next.js bez połączenia z NATS-em, więc jego konsument
 * zużycia surowca żyje w nexus-core (`ProvisioStockConsumer`). Filtr durable
 * consumera MUSI być tym samym subjectem, który publikuje producent — trzymamy
 * go tutaj, obok słownika, żeby nie rozjechał się z `FABRICA_EVENTS`
 * (test kontraktowy w services/nexus-core/src/provisio/__tests__ pilnuje
 * równości z `FABRICA_EVENTS.ingredientConsumed('*')`).
 */
export const PROVISIO_SUBSCRIPTIONS = {
  /** Zużycie surowca w produkcji, wszyscy tenanci → dekrementacja stanu. */
  allIngredientConsumed: () => 'tenant.*.events.fabrica.ingredient_consumed',
} as const;

/**
 * Fabrica Module Events
 * Production management - scheduling, execution, quality, forecasting
 */
export const FABRICA_EVENTS = {
  // Production Order events
  productionOrderCreated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'production_order_created'),
  productionOrderStarted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'production_order_started'),
  productionOrderCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'production_order_completed'),
  productionOrderPaused: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'production_order_paused'),
  productionOrderCancelled: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'production_order_cancelled'),

  // Stage events
  stageStarted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'stage_started'),
  stageCompleted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'stage_completed'),
  stageFailed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'stage_failed'),

  // Quality events
  qualityCheckPassed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'quality_check_passed'),
  qualityCheckFailed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'quality_check_failed'),

  // Ingredient/material events
  ingredientConsumed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'ingredient_consumed'),
  materialShortage: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'material_shortage'),

  // Schedule events
  schedulePublished: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'schedule_published'),
  scheduleAdjusted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'schedule_adjusted'),

  // Equipment events
  equipmentStarted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'equipment_started'),
  equipmentStopped: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'equipment_stopped'),
  equipmentAlarm: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'equipment_alarm'),

  // Waste events
  wasteRecorded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'waste_recorded'),

  // Batch events
  batchCompleted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'batch_completed'),
  labelPrinted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'label_printed'),
  palletCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'pallet_completed'),

  // KPI events
  kpiUpdated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'kpi_updated'),

  // Forecast events
  forecastGenerated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'forecast_generated'),

  // Migration events
  migrationStarted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'migration_started'),
  migrationCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'migration_completed'),
  migrationFailed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'migration_failed'),

  // Nexus AI decisions
  nexusDecision: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'nexus_decision'),

  // Zdarzenia robotów NIE mieszkają już w tej rodzinie. `robotSessionStarted`
  // i `robotSessionCompleted` budowały `…events.fabrica.robot_session_*`, nie
  // miały ANI producenta, ANI konsumenta i przez to wyglądały na ścieżkę
  // zdarzeń robota, którą nie były — prawdziwa to `tenant.*.events.robot.>`
  // (`ROBOT_EVENT_SUBSCRIPTION` w `robot.ts`), a decyzje produktowe organu
  // jadą teraz przez `ARTIFEX_EVENTS` niżej w tym pliku.

  // Sourdough culture events
  sourdoughCultureCreated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_culture_created'),
  sourdoughStatusChanged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_status_changed'),
  sourdoughCultureDead: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_culture_dead'),
  sourdoughFed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_fed'),
  sourdoughFeedingOverdue: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_feeding_overdue'),
  sourdoughPhRecorded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_ph_recorded'),
  sourdoughPhAlert: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_ph_alert'),
  sourdoughSensoryRecorded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_sensory_recorded'),
  sourdoughPhaseChanged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_phase_changed'),
  sourdoughStarterReady: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_starter_ready'),
  sourdoughOverfermented: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_overfermented'),
  sourdoughRescueStarted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_rescue_started'),
  sourdoughRescueCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_rescue_completed'),
  sourdoughRuleTriggered: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'sourdough_rule_triggered'),

  // Okna czasowe łańcucha produkcyjnego (Strażnik Okien Czasowych — wątek W7
  // planu ORGANIZM; emisja WYŁĄCZNIE przy przejściu werdyktu, nigdy co tick).
  timeWindowClosing: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'time_window_closing'),
  timeWindowExceeded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'time_window_exceeded'),
  chainSlackChanged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'chain_slack_changed'),

  // Protokół dyrygenta na stanowisku (fabricaDirective — wątek W13): wejście
  // w takt, potwierdzenie i odmowa panelu wracają na magistralę jako fakt.
  taktEntered: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'takt_entered'),
  taktCommitted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'takt_committed'),
  taktRefused: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'takt_refused'),

  // Grafik LexShift jako pojemność planera (wątek W14): wykryty konflikt jest
  // SYGNAŁEM do Skrzynki Decyzji — planer sam niczego nie przesuwa.
  scheduleConflictDetected: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'schedule_conflict_detected'),

  // Most WaterAndIce (wątek W16): sugestia temperatury wody dla etapu mieszania
  // liczona jednym silnikiem (ADR-004); VERIFY = waterandice.temperature_recorded.
  waterTempSuggested: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'fabrica', 'water_temp_suggested'),

  // Dozór floty paneli (`dozorFloty.ts`): stanowisko, które przestało być
  // sprawne. Emisja WYŁĄCZNIE przy zmianie zestawu usterek albo raz na dobę,
  // nigdy co przebieg — ten sam reżim, co Strażnik Okien wyżej. Nazwa jest
  // angielska jak cała ta rodzina, choć kod po drugiej stronie mówi po polsku.
  panelFault: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'panel_fault'),

  // Dozór wzorcowań (`dozorWzorcowan.ts`): świadectwo przyrządu, które zaraz
  // wygaśnie albo już wygasło. Emisja przy ZMIANIE STANU albo raz na tydzień —
  // rzadziej niż `panel_fault`, bo świadectwa nie da się odnowić w godzinę.
  calibrationDue: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'fabrica', 'calibration_due'),
} as const;

/**
 * FURNUS — organ pieca: komory, programy, plan dnia z wersjami, wsady, zadania przy piecu.
 *
 * Fabrica kończy łańcuch NA piecu (kotwica „wypiek”); FURNUS prowadzi to, co dzieje się
 * W piecu i przy nim: który wsad do której komory, kiedy przenagrzać, kiedy rozładować,
 * czy plan zdąży na deadline. Producentem tematów jest skrzynka nadawcza organu
 * (`furnus_event_outbox`, kształt 616/619, drenowana przez websocket-gateway) — tematy
 * stoją tu wcześniej niż producent, żeby panel, aplikacja natywna i Nexus mówiły jednym
 * słownikiem od pierwszej migracji.
 *
 * NAZWY W CZASIE PRZESZŁYM, JEDEN SEGMENT: `batch_loaded`, nie `batch.loaded` — kropka
 * dołożyłaby szósty segment i temat wypadłby z `tenant.*.events.furnus.*` bez błędu po
 * drodze (`publish.ts` odrzuca go dopiero w runtime). Wyjątek od czasu przeszłego:
 * `plan_deadline_at_risk` i `day_summary` opisują STAN w chwili emisji, nie czynność.
 */
export const FURNUS_EVENTS = {
  // Plan dnia: publikacja wersji, przeliczenie po faktach, prognoza po deadline
  planPublished: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'plan_published'),
  planReplanned: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'plan_replanned'),
  planDeadlineAtRisk: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'furnus', 'plan_deadline_at_risk'),

  // Wsady — fakty fizyczne z urządzenia przy piecu (per komora, z własnym czasem)
  batchLoaded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'batch_loaded'),
  batchUnloaded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'batch_unloaded'),
  batchAdjusted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'batch_adjusted'),
  batchCancelled: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'batch_cancelled'),
  batchDelayed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'batch_delayed'),

  // Zadania przy piecu (dzielenie, formowanie, nacinanie…)
  taskCompleted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'task_completed'),
  taskSkipped: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'task_skipped'),
  taskMissed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'task_missed'),

  // Zgłoszenia „Mam problem” i alerty z reguł organizacji
  issueCreated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'issue_created'),
  issueUpdated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'issue_updated'),
  alertTriggered: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'alert_triggered'),
  alertAcknowledged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'furnus', 'alert_acknowledged'),

  // Podsumowanie dnia (`kind`: deadline | shift_end), jakość (4 ujęcia), dostępność komór
  daySummary: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'day_summary'),
  qualityPhotoAdded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'furnus', 'quality_photo_added'),
  chamberDisabled: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'furnus', 'chamber_disabled'),
  chamberEnabled: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'chamber_enabled'),

  // CCP wypieku (W-30): próg w miąższu osiągnięty (sonda), CCP niezaliczone
  // (poniżej progu albo brak pomiaru), odchylenie zamknięte decyzją technologa
  coreTempReached: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'furnus', 'core_temp_reached'),
  ccpFailed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'ccp_failed'),
  ccpResolved: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'furnus', 'ccp_resolved'),
} as const;

/**
 * ARTIFEX — organ robotów i ich edukacji (Akademia Zawodów, flota, twin).
 *
 * DWIE RODZINY, NIE JEDNA — I TO JEST ROZSTRZYGNIĘCIE, NIE NIEDOPATRZENIE.
 *
 * `tenant.{org}.events.robot.*` (`ROBOT_EVENTS` w `robot.ts`) opisuje SPRZĘT:
 * epizod się nagrał, trening się skończył. Producentem jest Python
 * (`robot-edge-agent`, `isaac-trainer`), a wiązanie Python↔TypeScript to
 * literał tekstowy, nie import — zmiana nazwy po jednej stronie jest zmianą
 * CICHĄ (koperta dochodzi, konsument odrzuca ją jako „nie werdykt", tabela
 * zostaje pusta i wygląda to jak „nikt nic nie zlecił"). Ten błąd kosztował
 * już fazę T1. Dlatego tamtej rodziny NIE RUSZAMY.
 *
 * `tenant.{org}.events.artifex.*` (poniżej) opisuje DECYZJE ORGANU: co
 * kurator orzekł, co wpięto do zbioru uczącego, co dopuszczono na sprzęt.
 * To są zdarzenia produktowe — słucha ich Nexus, powiadomienia i graf — i nie
 * mają nic wspólnego z tym, czy robot w ogóle istnieje.
 *
 * NAZWY W CZASIE PRZESZŁYM i BEZ KROPKI w członie zdarzenia: `{module}`
 * i `{event}` to DOKŁADNIE po jednym segmencie, a `publish.ts` odrzuca
 * wszystko inne. Kropka dołożyłaby szósty segment i temat wypadłby
 * z wieloznacznika `tenant.*.events.artifex.*` — bez błędu po drodze.
 */
export const ARTIFEX_EVENTS = {
  /** Demonstracja wpięta do czynności — materiał wszedł do zbioru uczącego. */
  demonstrationLinked: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'artifex', 'demonstration_linked'),
  /** Kurator ocenił ujęcie: przydatne albo odrzucone z kodem powodu. */
  demonstrationJudged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'artifex', 'demonstration_judged'),
  /** Zlecono przebieg symulacji (kosztuje GPU — dlatego jest zdarzeniem). */
  episodeRequested: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'artifex', 'episode_requested'),
  /** Zlecono trening polityki — jedyna trasa organu, która kosztuje pieniądze. */
  trainingRequested: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'artifex', 'training_requested'),
  /** Polityka przeszła bramkę `twin_sim_runs` i weszła do katalogu. */
  policyAdmitted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'artifex', 'policy_admitted'),
  /** Czynność awansowała na wyższy poziom dojrzałości w katalogu zawodów. */
  activityLevelRaised: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'artifex', 'activity_level_raised'),
  /** Pracownik wycofał zgodę — demonstracje wypadają, polityki do przetrenowania. */
  consentWithdrawn: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'artifex', 'consent_withdrawn'),
} as const;

/** Wszystkie zdarzenia organu, dowolny najemca — filtr konsumentów trwałych. */
export const ARTIFEX_SUBSCRIPTIONS = {
  allEvents: () => 'tenant.*.events.artifex.>',
} as const;

/**
 * Nexus AI Module Events
 * AI decision-making, knowledge management, skills, policies
 */
export const NEXUS_EVENTS = {
  // Chat/conversation events
  chatMessageSent: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'chat_message_sent'),
  chatResponseGenerated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'chat_response_generated'),

  // Decision events
  decisionMade: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'nexus', 'decision_made'),
  decisionApproved: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'decision_approved'),
  decisionRejected: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'decision_rejected'),
  // VERIFY pomiarem skutku (wątek W4): decyzja przechodzi z 'verifying' na
  // completed/failed dopiero po pomiarze — nigdy auto-completed.
  decisionVerified: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'decision_verified'),

  // Skill events
  skillTriggered: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'nexus', 'skill_triggered'),
  skillCompleted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'nexus', 'skill_completed'),
  skillFailed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'nexus', 'skill_failed'),

  // Knowledge events
  knowledgeUpdated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'knowledge_updated'),
  knowledgeIndexed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'knowledge_indexed'),

  // Policy events
  policyViolation: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'nexus', 'policy_violation'),
  policyUpdated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'nexus', 'policy_updated'),

  // Usage events
  tokenUsageRecorded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'token_usage_recorded'),
  budgetThresholdReached: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'budget_threshold_reached'),

  // Education events — Nexus zaprasza partnera / osobę z zespołu na rozmowę
  // edukacyjną, egzamin ustny albo quiz. Konsumenci: pocztaZdarzen (e-mail)
  // i novu-bridge (in-app).
  edukacjaZaproszenie: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'edukacja_zaproszenie'),
  // Zaproszenie na szkolenie produktowe (Akademia) — pełny onboarding
  // platformy poprzedzający egzaminy. Ci sami konsumenci co wyżej.
  szkolenieZaproszenie: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'szkolenie_zaproszenie'),
  // Certyfikat modułowy partnera dobiega końca ważności (fala P7 planu 45).
  // Ci sami konsumenci: pocztaZdarzen (e-mail) i novu-bridge (in-app).
  // Tłumik NIE jest tutaj — wydaje i oznacza go baza
  // (`partner_certyfikaty_do_przypomnienia`, migracja 721), więc jedno
  // zdarzenie = jedno przypomnienie, także przy dwóch przebiegach naraz.
  certyfikatWygasa: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'nexus', 'certyfikat_wygasa'),
} as const;

/**
 * Nexus AI Assistant Gateway Events
 * Multi-channel messaging and AI agent orchestration
 */
export const NEXUS_ASSISTANT_EVENTS = {
  // Message events
  messageReceived: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'openclaw', 'message_received'),
  messageSent: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'openclaw', 'message_sent'),
  messageDelivered: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'openclaw', 'message_delivered'),
  messageFailed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'openclaw', 'message_failed'),

  // Channel events
  channelConnected: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'openclaw', 'channel_connected'),
  channelDisconnected: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'openclaw', 'channel_disconnected'),
  channelError: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'openclaw', 'channel_error'),

  // Session events
  sessionCreated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'openclaw', 'session_created'),
  sessionExpired: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'openclaw', 'session_expired'),
  sessionPaired: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'openclaw', 'session_paired'),

  // Skill events
  skillExecuted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'openclaw', 'skill_executed'),
  skillFailed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'openclaw', 'skill_failed'),

  // Agent events
  agentSpawned: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'openclaw', 'agent_spawned'),
  agentCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'openclaw', 'agent_completed'),
  agentFailed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'openclaw', 'agent_failed'),
} as const;

// =============================================================================
// AI NERVOUS SYSTEM EVENT FAMILIES (WP3)
// =============================================================================
//
// These families route through the existing `NATS_SUBJECTS.event(...)` builder
// so they land under `tenant.{org}.events.{category}.*` and are therefore
// caught by the existing `tenant.*.events.>` wildcard (the single `EVENTS`
// stream — see WP1). They are realized as FILTERED durable consumers on that
// stream, NOT as separate streams (separate streams with overlapping subjects
// would be rejected by `streams.add`). No new builder is introduced.
// =============================================================================

/**
 * Genesis Events
 * Tenant onboarding / domain-pack interview & graph seeding lifecycle.
 * Subjects: tenant.{org}.events.genesis.*
 */
export const GENESIS_EVENTS = {
  interviewStarted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'genesis', 'interview_started'),
  interviewAnswered: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'genesis', 'interview_answered'),
  interviewCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'genesis', 'interview_completed'),
  domainPackLoaded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'genesis', 'domain_pack_loaded'),
  graphSeeded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'genesis', 'graph_seeded'),
  personaGenerated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'genesis', 'persona_generated'),
  onboardingCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'genesis', 'onboarding_completed'),
} as const;

/**
 * Memory Events
 * Emitted by the encoder/consolidator as stimuli become durable memory.
 * Subjects: tenant.{org}.events.memory.*
 */
export const MEMORY_EVENTS = {
  encoded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'memory', 'encoded'),
  embedded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'memory', 'embedded'),
  consolidated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'memory', 'consolidated'),
  recalled: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'memory', 'recalled'),
  decayed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'memory', 'decayed'),
  forgotten: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'memory', 'forgotten'),
  nodeUpserted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'memory', 'node_upserted'),
  relationUpserted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'memory', 'relation_upserted'),
} as const;

/**
 * Success Events — the "żywa dokumentacja sukcesu" (Living Success Chronicle).
 *
 * NEXUS keeps a LIVING record of every tenant's journey along four axes —
 * BEFORE (baseline captured at Genesis), NOW (periodic pulses of current
 * metrics), AFTER (milestones/wins recorded as the organism operates) and the
 * FUTURE (projections toward the tenant's targets). These entries are the raw
 * material for per-tenant marketing / case studies AND for presenting live
 * results to the organization owner.
 *
 * Like every other family here they route through `NATS_SUBJECTS.event(...)` so
 * they land under `tenant.{org}.events.success.*` and are caught by the single
 * EVENTS stream → the generic Encoder materializes them into durable memory
 * (agent_memories + kg_nodes), which is exactly what makes the documentation
 * "live in memory" and become recallable into chat.
 * Subjects: tenant.{org}.events.success.*
 */
export const SUCCESS_EVENTS = {
  // "BEFORE" — the starting state, captured once when Genesis completes.
  baselineCaptured: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'success', 'baseline_captured'),
  // "AFTER" — a concrete win / milestone the organism delivered.
  milestoneRecorded: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'success', 'milestone_recorded'),
  // "NOW" — a periodic pulse snapshot of the tenant's current metrics.
  pulseRecorded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'success', 'pulse_recorded'),
  // "FUTURE" — a projection toward a target the tenant is steering for.
  projectionUpdated: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'success', 'projection_updated'),
} as const;

/**
 * Render Events
 * AI-driven render-directives (whitelisted widget/visualization output).
 * Subjects: tenant.{org}.events.render.*
 */
export const RENDER_EVENTS = {
  directiveEmitted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'render', 'directive_emitted'),
  widgetUpdated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'render', 'widget_updated'),
  dashboardComposed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'render', 'dashboard_composed'),
  directiveRejected: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'render', 'directive_rejected'),
} as const;

/**
 * Display Events (TV Display Runtime — J.2)
 * Announcements + config pushes to wall screens (`apps/insolitum-display`).
 * Payloads: `DisplayAnnouncement` / `DisplayDismiss` (see display.ts) —
 * validated on the screen with `validateAnnouncement` before rendering.
 * Subjects: tenant.{org}.events.display.* — additive, caught by the existing
 * `tenant.*.events.>` convention (JetStream EVENTS → encoder → memory), so
 * every announcement Nexus makes becomes part of the organism's memory.
 * Spec: docs/02-ARCHITECTURE/TV_DISPLAY_RUNTIME.md §3.1
 */
export const DISPLAY_EVENTS = {
  announce: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'display', 'announce'),
  dismiss: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'display', 'dismiss'),
  configUpdated: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'display', 'config_updated'),
} as const;

/**
 * Health Events
 * Self-monitoring of the nervous system (encoder lag, embed/recall budgets,
 * kill-switch / guardrail state).
 * Subjects: tenant.{org}.events.health.*
 */
export const HEALTH_EVENTS = {
  heartbeat: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'health', 'heartbeat'),
  encoderLag: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'health', 'encoder_lag'),
  budgetExceeded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'health', 'budget_exceeded'),
  degraded: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'health', 'degraded'),
  recovered: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'health', 'recovered'),
  killSwitchEngaged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'health', 'kill_switch_engaged'),
  killSwitchReleased: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'health', 'kill_switch_released'),
  // Faza 6 — self-healing PROPOSALS (human-gated, NO actuation). Emitted when
  // the organism NOTICES a problem and PROPOSES a deterministic remediation that
  // a human must approve & execute out-of-band. The event carries a
  // `health.remediation` entity so it materializes as a connected, approvable
  // node in the Living Network. Subject: tenant.{org}.events.health.remediation_proposed
  remediationProposed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'health', 'remediation_proposed'),
  // Faza 6 — human DECISION on a proposed remediation (approved/rejected/dismissed).
  // DATA + EVENT ONLY — this event NEVER executes the remediation; 'executed' is
  // reserved for a future, separately-gated executor (strefa zakazana).
  // Subject: tenant.{org}.events.health.remediation_decided
  remediationDecided: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'health', 'remediation_decided'),
} as const;

/**
 * Code Task Events
 * Autonomous code-generation / self-improvement task lifecycle.
 * Subjects: tenant.{org}.events.code.*
 */
export const CODE_TASK_EVENTS = {
  taskQueued: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'code', 'task_queued'),
  taskStarted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'code', 'task_started'),
  taskCompleted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'code', 'task_completed'),
  taskFailed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'code', 'task_failed'),
  prOpened: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'code', 'pr_opened'),
  prMerged: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'code', 'pr_merged'),
  reviewRequested: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'code', 'review_requested'),
} as const;

/**
 * Platform Events
 * Observational self-description of the platform's own anatomy (services +
 * engines) as a CONNECTED subgraph in the Living Network. Emitted by nexus-core
 * on start + a low-frequency interval. ADDITIVE only — never actuates.
 * Subjects: tenant.{org}.events.platform.*
 */
export const PLATFORM_EVENTS = {
  topology: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'platform', 'topology'),
} as const;

/**
 * Steering Events
 * AI steering signals (intent / guidance toward actuators & modules).
 * Subjects: tenant.{org}.events.steering.*
 */
export const STEERING_EVENTS = {
  intentEmitted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'steering', 'intent_emitted'),
  goalSet: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'steering', 'goal_set'),
  goalReached: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'steering', 'goal_reached'),
  correctionApplied: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'steering', 'correction_applied'),
  overridden: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'steering', 'overridden'),
  // Faza 7 — device-command PROPOSALS (human-gated, NO dispatch/actuation).
  // Emitted when the super-lider PROPOSES a controllable device command a human
  // must approve. This is an EVENTS-stream subject (the encoder consumes it);
  // it is NOT a `commands.*` (NATS_SUBJECTS.command) actuation subject — nothing
  // is ever dispatched to a device. The event carries a `steering.command` entity
  // so it materializes as a connected, approvable node in the Living Network.
  // Subject: tenant.{org}.events.steering.command_proposed
  commandProposed: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'steering', 'command_proposed'),
  // Faza 7 — human DECISION on a proposed opportunity (approved/rejected).
  // DATA + EVENT ONLY — nothing is actuated (strefa zakazana). Carries the
  // steering.opportunity entity so the decision lands on the SAME Living Network
  // node the proposal created. Subject: tenant.{org}.events.steering.decision
  decisionMade: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'steering', 'decision'),
} as const;

/**
 * Actuation Events
 * Outcomes of acting on the physical/digital world (effectors/devices).
 * Subjects: tenant.{org}.events.actuation.*
 */
export const ACTUATION_EVENTS = {
  commandIssued: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'actuation', 'command_issued'),
  commandAcknowledged: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'actuation', 'command_acknowledged'),
  commandCompleted: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'actuation', 'command_completed'),
  commandFailed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'actuation', 'command_failed'),
  commandRejected: (tenantId: string) =>
    NATS_SUBJECTS.event(tenantId, 'actuation', 'command_rejected'),
} as const;

/**
 * Print Events (Część K — Centralna Platforma Druku)
 * Druk jako ORGAN FIZYCZNY: każde zlecenie wydruku płynie przez te zdarzenia →
 * JetStream → encoder → pamięć („drukarka X pada w 15% zadań", „szablon Y często
 * reprintowany"). Awarie zasilają homeostazę (Filar 7: re-route/re-queue/reaper).
 * Emitowane przez `services/print-core`. Subjects: tenant.{org}.events.print.*
 * Additive — nie zmienia istniejących producentów.
 */
export const PRINT_EVENTS = {
  jobQueued: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'print', 'job_queued'),
  printed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'print', 'printed'),
  failed: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'print', 'failed'),
  reprinted: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'print', 'reprinted'),
} as const;

/**
 * Presence Events
 * Liveness/availability of agents, devices, channels and sessions.
 * Subjects: tenant.{org}.events.presence.*
 */
export const PRESENCE_EVENTS = {
  online: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'presence', 'online'),
  offline: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'presence', 'offline'),
  joined: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'presence', 'joined'),
  left: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'presence', 'left'),
  heartbeat: (tenantId: string) => NATS_SUBJECTS.event(tenantId, 'presence', 'heartbeat'),
} as const;

/**
 * `tenant.{org}.commands.device.{deviceId}.{action}` — SZEŚĆ segmentów.
 *
 * DLACZEGO TO ISTNIEJE OBOK `DEVICE_COMMANDS`. Jedyny konsument komend urządzeń
 * w całej platformie — `parseCommandSubject` w
 * `services/fabrica-gateway-quic/src/bridge/topic-mapper.ts` — subskrybuje
 * `tenant.*.commands.device.*.*` i ODRZUCA wszystko, co nie ma identyfikatora
 * urządzenia (jego własny test to potwierdza). Tymczasem `DEVICE_COMMANDS`
 * poniżej buduje kształt PIĘCIOsegmentowy, bez `deviceId`. Komenda zbudowana
 * tamtymi helperami nie dociera nigdzie i nie zgłasza tego błędem — po prostu
 * nikt jej nie słucha.
 *
 * `services/fabrica-core/src/nats/subjects.ts` obszedł to lokalnym builderem dla
 * wag, zostawiając w komentarzu warunek: „do czasu uzgodnienia słownika
 * kanonicznego". To jest to uzgodnienie — builder niezależny od rodzaju
 * urządzenia, więc humanoid i waga mówią jednym kształtem (L.9: heterogeniczna
 * flota różni się adapterem, nie kontraktem).
 *
 * `DEVICE_COMMANDS` zostaje NIEUSUNIĘTY i to jest decyzja: usunięcie zmieniłoby
 * publiczne API pakietu przy okazji zmiany o innym celu. Jest za to oznaczony
 * jako przestarzały, żeby następny autor nie sięgnął po niego w dobrej wierze.
 */
export function deviceCommandSubject(tenantId: string, deviceId: string, action: string): string {
  return `tenant.${tenantId}.commands.device.${deviceId}.${action}`;
}

/** Wzorzec subskrypcji dla mostu urządzeń — dokładnie ten, którego używa brama. */
export const DEVICE_COMMAND_SUBSCRIPTION = 'tenant.*.commands.device.*.*';

/**
 * `tenant.{org}.commands.panel.{licenseId}.{action}` — SZEŚĆ segmentów,
 * rodzina PANEL (kanał zwrotny na stanowisko — wątek W13 planu ORGANIZM).
 *
 * Rodzina jest CELOWO rozłączna z `commands.device.*.*`: jedyny konsument
 * komend device (`parseCommandSubject` w fabrica-gateway-quic) subskrybuje
 * `tenant.*.commands.device.*.*` i komendy panelowe go NIE dotykają — brama
 * QUIC nie wymaga żadnej zmiany. Panel adresujemy po `licenseId`
 * z `device_licenses` (kartoteka wspólna: to samo id niesie OTA i sesję
 * socketa; websocket-gateway bierze je WYŁĄCZNIE z claims JWT sesji DEVICE).
 *
 * Tor gwarantowany dyrektyw (skrzynka w `fabrica_panel_stanowisko`, 30 s)
 * działa bez tej rodziny — push jest przyspieszeniem, nie warunkiem.
 */
export function panelCommandSubject(tenantId: string, licenseId: string, action: string): string {
  return `tenant.${tenantId}.commands.panel.${licenseId}.${action}`;
}

/** Wzorzec subskrypcji komend panelu — filtr sesji DEVICE w websocket-gateway. */
export const PANEL_COMMAND_SUBSCRIPTION = 'tenant.*.commands.panel.*.*';

/**
 * Device Commands
 * Outbound commands to physical devices, routed via the existing
 * `NATS_SUBJECTS.command(...)` builder → tenant.{org}.commands.device.{action}.
 *
 * @deprecated Buduje PIĘĆ segmentów, bez `deviceId` — a jedyny konsument
 * (`parseCommandSubject`) wymaga sześciu i odrzuca resztę. Komendy wysłane tymi
 * helperami giną po cichu. Używaj `deviceCommandSubject(tenant, deviceId, action)`.
 */
export const DEVICE_COMMANDS = {
  setState: (tenantId: string) => NATS_SUBJECTS.command(tenantId, 'device', 'set_state'),
  reboot: (tenantId: string) => NATS_SUBJECTS.command(tenantId, 'device', 'reboot'),
  calibrate: (tenantId: string) => NATS_SUBJECTS.command(tenantId, 'device', 'calibrate'),
  updateFirmware: (tenantId: string) =>
    NATS_SUBJECTS.command(tenantId, 'device', 'update_firmware'),
  setThreshold: (tenantId: string) => NATS_SUBJECTS.command(tenantId, 'device', 'set_threshold'),
} as const;

/**
 * Cross-module event subscriptions
 * Defines which modules subscribe to events from other modules
 */
export const CROSS_MODULE_SUBSCRIPTIONS = {
  // NeuroSense subscribes to LexShift clock events to activate sensors
  neurosenseFromLexshift: (tenantId: string) => [
    LEXSHIFT_EVENTS.employeeClockedIn(tenantId),
    LEXSHIFT_EVENTS.employeeClockedOut(tenantId),
  ],

  // Gamification subscribes to all module activity events for point awards
  gamificationFromAll: (tenantId: string) => [
    LEXSHIFT_EVENTS.employeeClockedIn(tenantId),
    LEXSHIFT_EVENTS.employeeClockedOut(tenantId),
    WATERANDICE_EVENTS.doughStarted(tenantId),
    WATERANDICE_EVENTS.doughReady(tenantId),
    WATERANDICE_EVENTS.temperatureRecorded(tenantId),
    AGENDA_EVENTS.taskCompleted(tenantId),
    PROVISIO_EVENTS.orderDelivered(tenantId),
    FABRICA_EVENTS.productionOrderCompleted(tenantId),
    FABRICA_EVENTS.stageCompleted(tenantId),
    FABRICA_EVENTS.qualityCheckPassed(tenantId),
    FABRICA_EVENTS.batchCompleted(tenantId),
  ],

  // USUNIĘTE: `provisioFromAgenda` (agenda.task_completed → zużycie magazynu).
  // Zadanie Agendy nie niesie ŻADNEGO odniesienia do pozycji magazynowej — ani
  // `agenda_tasks`, ani `agenda_task_assignments`, ani `agenda_task_completions`
  // nie mają kolumny wiążącej je z `provisio_inventory` (sprawdzone na żywym
  // schemacie 2026-07-27). Konsument tej gałęzi nie miałby CZEGO zdjąć ze stanu,
  // a zgadywanie po nazwie zadania byłoby atrapą. Realne zużycie surowca płynie
  // z Fabriki (`provisioFromFabrica` → `fabrica.ingredient_consumed`), która ma
  // `stock_item_id` i ilość. Gałąź wróci dopiero, gdy Agenda dostanie własny
  // model materiałów (kartoteka zadania → lista pozycji + ilości).

  // Provisio subscribes to Fabrica material consumption for real-time stock tracking
  provisioFromFabrica: (tenantId: string) => [
    FABRICA_EVENTS.ingredientConsumed(tenantId),
    FABRICA_EVENTS.materialShortage(tenantId),
    FABRICA_EVENTS.productionOrderCreated(tenantId),
  ],

  // Fabrica subscribes to Provisio stock events for material availability
  fabricaFromProvisio: (tenantId: string) => [
    PROVISIO_EVENTS.stockReceived(tenantId),
    PROVISIO_EVENTS.lowStockAlert(tenantId),
    PROVISIO_EVENTS.outOfStockAlert(tenantId),
    PROVISIO_EVENTS.mrpProposalGenerated(tenantId),
  ],

  // Fabrica subscribes to LexShift for shift-based scheduling
  fabricaFromLexshift: (tenantId: string) => [
    LEXSHIFT_EVENTS.employeeClockedIn(tenantId),
    LEXSHIFT_EVENTS.employeeClockedOut(tenantId),
    LEXSHIFT_EVENTS.schedulePublished(tenantId),
  ],

  // Fabrica subscribes to NeuroSense for IoT-triggered production events
  fabricaFromNeurosense: (tenantId: string) => [
    NEUROSENSE_EVENTS.sensorAlert(tenantId),
    NEUROSENSE_EVENTS.sensorOffline(tenantId),
  ],

  // Agenda subscribes to Fabrica for auto-generated production tasks
  agendaFromFabrica: (tenantId: string) => [
    FABRICA_EVENTS.productionOrderCreated(tenantId),
    FABRICA_EVENTS.schedulePublished(tenantId),
    FABRICA_EVENTS.qualityCheckFailed(tenantId),
    FABRICA_EVENTS.equipmentAlarm(tenantId),
    FABRICA_EVENTS.sourdoughFeedingOverdue(tenantId),
    FABRICA_EVENTS.sourdoughRescueStarted(tenantId),
    FABRICA_EVENTS.sourdoughPhAlert(tenantId),
    FABRICA_EVENTS.sourdoughStarterReady(tenantId),
  ],

  // Nexus subscribes to all module events for AI context
  nexusFromAll: (tenantId: string) => NATS_SUBSCRIPTIONS.allTenantEvents(tenantId),

  // All modules can subscribe to Nexus decisions
  modulesFromNexus: (tenantId: string) => [
    NEXUS_EVENTS.decisionMade(tenantId),
    NEXUS_EVENTS.policyViolation(tenantId),
  ],

  // Universe Shell (dashboard) subscribes to all module events
  shellFromAll: (tenantId: string) => NATS_SUBSCRIPTIONS.allTenantEvents(tenantId),

  // Nexus AI Assistant subscribes to all module events for channel notifications
  nexusAssistantFromAll: (tenantId: string) => NATS_SUBSCRIPTIONS.allTenantEvents(tenantId),

  // Modules subscribe to Nexus AI Assistant events for message-triggered actions
  modulesFromNexusAssistant: (tenantId: string) => [
    NEXUS_ASSISTANT_EVENTS.messageReceived(tenantId),
    NEXUS_ASSISTANT_EVENTS.sessionPaired(tenantId),
  ],
} as const;

// =============================================================================
// MAGISTRALA ZAMÓWIEŃ — SŁOWNIK ZAMKNIĘTY (PR 1.8)
// =============================================================================
//
// Źródło: `docs/16-ZAMOWIENIA/02-MAGISTRALA-NATS.md` §4.1 (koperta, `msg_id`),
// §5 (tematy), §6 (strumienie i konsumenci), §7.2 (rodzina → producent).
// Dziennik: `docs/16-ZAMOWIENIA/realizacja/F1-1.8-orders-core.md`.
//
// ZASADA: temat spoza słownika NIE DA SIĘ ZBUDOWAĆ. Literówka w temacie NATS
// nie rzuca wyjątkiem — wiadomość po prostu do nikogo nie dociera (ten sam
// kształt usterki, co `DEVICE_COMMANDS` wyżej). Dlatego każdy budowniczy niżej
// ma typ zawężony do słownika ORAZ sprawdzenie w czasie wykonania: organ
// komendy przychodzi z danych (`organization_backends.system_id`, §5), więc
// sam typ by go nie zatrzymał. Bramki: `__tests__/magistralaZamowien.test.ts`
// (słownik), `tests/unit/packages/slownikMagistraliZamowien.test.ts` (nikt nie
// składa tych tematów ręcznie poza tym plikiem).
//
// TYLKO USŁUGI (`02` §12): indeks `@insolitum/types` ma importy z sufiksem
// `.js`, więc aplikacje Next bez `extensionAlias` tego nie zbudują; powłoka
// (Vite) i usługi — tak. Ta sekcja nie ma importów, żeby zostać samodzielna.
//
// `nats.ts` zmienia w Fali 1 wyłącznie PR 1.8 (ARCH §11.1) — stąd słownik
// obejmuje od razu rodziny Fal 2–4, a nie tylko to, co emituje Fala 2.
// =============================================================================

/** Organizacja w temacie: UUID małymi literami (`02` §5; CHECK tematu w `bus_outbox` porównuje z `organization_id::text`). */
const ORGANIZACJA_TEMATU_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Człon nazwy w słowniku: rodzina, zdarzenie, organ, akcja, usługa i metoda RPC. */
export const CZLON_NAZWY_RE = /^[a-z][a-z0-9_]*$/;

/** Pojedynczy człon identyfikatora (np. pojazdu) — bez kropek, wieloznaczników i białych znaków. */
const CZLON_IDENTYFIKATORA_RE = /^[a-z0-9][a-z0-9_-]*$/;

/** Klucz własny obiektu — `in` przepuściłby `toString`, `constructor`, `__proto__`. */
function maKlucz(obiekt: object, klucz: string): boolean {
  return Object.prototype.hasOwnProperty.call(obiekt, klucz);
}

function wymagajOrganizacji(organizacja: string): void {
  if (!ORGANIZACJA_TEMATU_RE.test(organizacja)) {
    throw new Error(
      `Temat magistrali: organizacja musi być UUID małymi literami, jest „${organizacja}”`
    );
  }
}

/**
 * Rodziny zdarzeń EVENTS publikowane przez outbox (`bus_outbox` → drainer
 * orders-core), z producentem z zamkniętej mapy `bus_rodziny` (`02` §7.2).
 * Tablica nazw każdej rodziny jest jedynym miejscem, w którym nazwa
 * zdarzenia istnieje; budowniczowie, filtry konsumentów, szkielety
 * kontraktów i `msg_id` biorą ją stąd.
 *
 * Poza słownikiem zostają świadomie: most samodzielnego Mandatum
 * ({@link MOST_MANDATUM}, tor bramy do F12), dotychczasowe `PROVISIO_EVENTS`
 * publikowane z przeglądarki (`02` §9, zamknięta lista przeglądarki z planem
 * przeniesienia na serwer) i istniejące rodziny innych modułów.
 */
export const RODZINY_ZDARZEN = {
  orders: {
    producent: 'orders-core',
    nazwy: [
      'order_received',
      'order_accepted',
      'order_rejected',
      'order_merged',
      'order_confirmed',
      'order_changed',
      'order_held',
      'order_released',
      'order_cancelled',
      'order_closed',
      'promise_date_changed',
      'fulfillment_order_created',
      'fulfillment_order_accepted',
      'fulfillment_order_rejected',
      'fulfillment_order_progressed',
      'fulfillment_order_shorted',
      'fulfillment_order_dispatched',
      'fulfillment_order_delivered',
      'fulfillment_order_failed',
      'fulfillment_order_cancelled',
      'production_demand_frozen',
      'production_demand_status_changed',
      'production_change_requested',
      'adjustment_registered',
      'batch_recall_opened',
      'partner_order_sent',
    ],
  },
  documents: {
    producent: 'orders-core',
    nazwy: [
      'document_requested',
      'document_issued',
      'document_fiscalized',
      'document_rejected',
      'document_unknown',
      'payment_matched',
      // D53-4 (decyzja właściciela 12.10.2026, ARCH „Korekta z 10.10.2026”):
      // rejestr dokumentów po `dokumenty_zarejestruj`, emisja w UD-12 planu 53.
      'document_registered',
    ],
  },
  integracje: {
    producent: 'orders-core',
    nazwy: ['intent_failed', 'agent_overdue', 'parity_mismatch', 'budget_breached'],
  },
  mandatum: {
    producent: 'mandatum-core',
    nazwy: [
      'wave_completed',
      'pick_confirmed',
      'short_pick',
      'loading_completed',
      'crate_loaded',
      'crate_missing',
      'crate_damaged',
      'route_run_started',
      'route_run_completed',
      'stop_delivered',
      'stop_problem',
      'container_moved',
      'adjustment_received',
      // Alert: tętno portu `poczta_wejsciowa` starsze niż 5 min w godzinach pracy.
      'intake_overdue',
    ],
  },
  packflow: {
    producent: 'packflow-core',
    nazwy: ['packing_started', 'package_packed'],
  },
  provisio: {
    producent: 'provisio',
    nazwy: [
      'reservation_created',
      'reservation_released',
      'material_shortage_detected',
      'purchase_proposal_created',
      'order_overdue',
      'production_received',
    ],
  },
} as const;

export type RodzinaZdarzen = keyof typeof RODZINY_ZDARZEN;
export type NazwaZdarzenia<R extends RodzinaZdarzen = RodzinaZdarzen> =
  (typeof RODZINY_ZDARZEN)[R]['nazwy'][number];
/** `{rodzina}.{nazwa}` — segmenty 4 i 5 tematu, ten sam kształt co klucze `KONTRAKTY_ZDARZEN`. */
export type KluczZdarzeniaMagistrali = {
  [R in RodzinaZdarzen]: `${R}.${NazwaZdarzenia<R>}`;
}[RodzinaZdarzen];
/** Producent z outboxu (`bus_outbox.producent`). */
export type ProducentOutboxu = (typeof RODZINY_ZDARZEN)[RodzinaZdarzen]['producent'];

/**
 * Most samodzielnego Mandatum (`02` §5, §7.2): cztery nazwy rodziny
 * `mandatum`, które do F12 publikuje brama torem serwisowym, wprost, bez
 * outboxu i bez `Idempotency-Key`. Nazwy są rozłączne z logistyką organu,
 * więc każda para (rodzina, nazwa) ma dokładnie jednego producenta.
 * Wyjątek znika razem z mostem (F12; przy D-24 po ostatnim najemcy
 * samodzielnym). Słownik nie buduje tych tematów — robi to brama z żądania.
 */
export const MOST_MANDATUM = {
  rodzina: 'mandatum',
  producent: 'gateway:service:mandatum',
  nazwy: ['order_created', 'order_status_changed', 'order_delivered', 'return_registered'],
  do: 'F12',
} as const;

/** Komendy `commands.organ.*` publikuje wyłącznie rdzeń (`02` §7.2). */
export const PRODUCENT_KOMEND_ORGANOW = 'orders-core' as const;

export function jestRodzinaZdarzen(rodzina: string): rodzina is RodzinaZdarzen {
  return maKlucz(RODZINY_ZDARZEN, rodzina);
}

export function jestZdarzeniemRodziny<R extends RodzinaZdarzen>(
  rodzina: R,
  nazwa: string
): nazwa is NazwaZdarzenia<R> {
  return (RODZINY_ZDARZEN[rodzina].nazwy as readonly string[]).includes(nazwa);
}

/**
 * Producent pary (rodzina, nazwa) według zamkniętej mapy `bus_rodziny`
 * (`02` §7.2), z wyjątkiem mostu. `null` = para spoza słownika — konsument
 * rdzenia odrzuca taką wiadomość jako obcego producenta rodziny (§9 izolacja (2)).
 */
export function producentZdarzenia(rodzina: string, nazwa: string): string | null {
  if (
    rodzina === MOST_MANDATUM.rodzina &&
    (MOST_MANDATUM.nazwy as readonly string[]).includes(nazwa)
  ) {
    return MOST_MANDATUM.producent;
  }
  if (!jestRodzinaZdarzen(rodzina) || !jestZdarzeniemRodziny(rodzina, nazwa)) return null;
  return RODZINY_ZDARZEN[rodzina].producent;
}

/** `tenant.{org}.events.{rodzina}.{nazwa}` — pięć członów (`02` §5). */
export function tematZdarzenia<R extends RodzinaZdarzen>(
  organizacja: string,
  rodzina: R,
  nazwa: NazwaZdarzenia<R>
): string {
  wymagajOrganizacji(organizacja);
  if (!jestRodzinaZdarzen(rodzina) || !jestZdarzeniemRodziny(rodzina, nazwa)) {
    throw new Error(`Temat magistrali: „${rodzina}.${nazwa}” spoza słownika zdarzeń`);
  }
  return `tenant.${organizacja}.events.${rodzina}.${nazwa}`;
}

/** Filtr konsumenta jednego zdarzenia wszystkich organizacji: `tenant.*.events.{rodzina}.{nazwa}`. */
export function filtrZdarzenia<R extends RodzinaZdarzen>(
  rodzina: R,
  nazwa: NazwaZdarzenia<R>
): string {
  if (!jestRodzinaZdarzen(rodzina) || !jestZdarzeniemRodziny(rodzina, nazwa)) {
    throw new Error(`Filtr magistrali: „${rodzina}.${nazwa}” spoza słownika zdarzeń`);
  }
  return `tenant.*.events.${rodzina}.${nazwa}`;
}

/**
 * Filtr całej rodziny: `tenant.*.events.{rodzina}.>`. Wieloznacznik wyłącznie
 * jako cały człon — `fulfillment_order_*` byłoby w NATS literałem, który
 * serwer przyjmie i który nie dopasuje niczego (`02` §6).
 */
export function filtrRodziny(rodzina: RodzinaZdarzen): string {
  if (!jestRodzinaZdarzen(rodzina)) {
    throw new Error(`Filtr magistrali: rodzina „${rodzina}” spoza słownika`);
  }
  return `tenant.*.events.${rodzina}.>`;
}

/** `ORDERS_EVENTS.orderAccepted` dla `order_accepted` — typ i wartość liczone tą samą regułą. */
type Wielblad<S extends string> = S extends `${infer Glowa}_${infer Ogon}`
  ? `${Glowa}${Capitalize<Wielblad<Ogon>>}`
  : S;

export type BudowniczowieRodziny<R extends RodzinaZdarzen> = {
  readonly [N in NazwaZdarzenia<R> as Wielblad<N>]: (organizacja: string) => string;
};

function wielblad(nazwa: string): string {
  return nazwa.replace(/_([a-z0-9])/g, (_, litera: string) => litera.toUpperCase());
}

function budowniczowieRodziny<R extends RodzinaZdarzen>(rodzina: R): BudowniczowieRodziny<R> {
  const wynik: Record<string, (organizacja: string) => string> = {};
  for (const nazwa of RODZINY_ZDARZEN[rodzina].nazwy as readonly NazwaZdarzenia<R>[]) {
    wynik[wielblad(nazwa)] = (organizacja: string) => tematZdarzenia(organizacja, rodzina, nazwa);
  }
  return Object.freeze(wynik) as BudowniczowieRodziny<R>;
}

/** Zdarzenia rdzenia zamówień (`orders-core`). */
export const ORDERS_EVENTS = budowniczowieRodziny('orders');
/** Zdarzenia rejestru dokumentów i intencji (`orders-core`, po wyniku adaptera). */
export const DOCUMENTS_EVENTS = budowniczowieRodziny('documents');
/** Alerty integracji (`orders-core`). */
export const INTEGRACJE_EVENTS = budowniczowieRodziny('integracje');
/** Logistyka organu Mandatum (`mandatum-core`, outbox) — bez nazw mostu ({@link MOST_MANDATUM}). */
export const MANDATUM_EVENTS = budowniczowieRodziny('mandatum');
/** Pakowanie (`packflow-core`, outbox). */
export const PACKFLOW_EVENTS = budowniczowieRodziny('packflow');
// Zdarzenia outboxu Provisio budują `tematZdarzenia(org, 'provisio', …)`
// i `filtrZdarzenia`: `PROVISIO_EVENTS` (wyżej) to dotychczasowe nazwy
// z przeglądarki, a mieszanie dwóch torów w jednym obiekcie ukryłoby, które
// z nich są w słowniku zamkniętym.

/**
 * Komendy organów `commands.organ.{organ}.{akcja}` (`02` §5) — sześć członów,
 * rozłączne z `commands.device` i `commands.panel`. Producent: wyłącznie
 * `orders-core`. `integracje` i `kanal` to porty (konsument `integracje-core`),
 * nie organy z `organization_backends`, ale mieszkają w tym samym strumieniu.
 */
export const KOMENDY_ORGANOW = {
  mandatum: ['fulfillment_request', 'fulfillment_cancel_request', 'fulfillment_change_request'],
  packflow: ['fulfillment_request', 'fulfillment_cancel_request', 'fulfillment_change_request'],
  fabrica: ['demand_submit', 'demand_change', 'demand_cancel', 'demand_plan'],
  receptor: ['demand_submit', 'demand_cancel'],
  arkusz_produkcyjny: ['demand_submit'],
  provisio: ['plan_supply', 'batch_block'],
  integracje: ['intent_ready'],
  kanal: ['fulfillment_confirm', 'cancel_confirm'],
} as const;

export type OrganKomend = keyof typeof KOMENDY_ORGANOW;
export type AkcjaOrganu<O extends OrganKomend = OrganKomend> = (typeof KOMENDY_ORGANOW)[O][number];
/** `{organ}.{akcja}` — segmenty 5 i 6 tematu komendy, klucz `KONTRAKTY_KOMEND`. */
export type KluczKomendy = { [O in OrganKomend]: `${O}.${AkcjaOrganu<O>}` }[OrganKomend];

/** Strażnik dla organu z danych (`organization_backends.system_id`) przed {@link organCommandSubject}. */
export function jestOrganKomend(organ: string): organ is OrganKomend {
  return maKlucz(KOMENDY_ORGANOW, organ);
}

export function jestAkcjaOrganu<O extends OrganKomend>(
  organ: O,
  akcja: string
): akcja is AkcjaOrganu<O> {
  return (KOMENDY_ORGANOW[organ] as readonly string[]).includes(akcja);
}

/**
 * `tenant.{org}.commands.organ.{organ}.{akcja}`. Organizacja tematu =
 * organizacja wykonawcy — także dla FO innej organizacji (3PL, partia
 * u opiekuna, `02` §9 izolacja (1)); kontrolę umowy robi `bus_wstaw`.
 */
export function organCommandSubject<O extends OrganKomend>(
  organizacja: string,
  organ: O,
  akcja: AkcjaOrganu<O>
): string {
  wymagajOrganizacji(organizacja);
  if (!jestOrganKomend(organ)) {
    throw new Error(`Komenda magistrali: organ „${organ}” spoza słownika komend`);
  }
  if (!jestAkcjaOrganu(organ, akcja)) {
    throw new Error(`Komenda magistrali: „${organ}.${akcja}” spoza słownika komend`);
  }
  return `tenant.${organizacja}.commands.organ.${organ}.${akcja}`;
}

/** Filtr konsumenta jednego organu: `tenant.*.commands.organ.{organ}.>` (filtry rozłączne per organ, `02` §6). */
export function filtrKomendOrganu(organ: OrganKomend): string {
  if (!jestOrganKomend(organ)) {
    throw new Error(`Filtr magistrali: organ „${organ}” spoza słownika komend`);
  }
  return `tenant.*.commands.organ.${organ}.>`;
}

/** Strumień komend organów (`02` §6) — zakłada wyłącznie orders-core, poza wspólnym `STREAM_DEFS`. */
export const STRUMIEN_KOMEND_ORGANOW = 'ORGAN_COMMANDS' as const;
/** Strumień advisory konsumentów rdzenia (`02` §6). */
export const STRUMIEN_ADVISORIES = 'ADVISORIES' as const;
/** Tematy strumienia `ORGAN_COMMANDS`. */
export const TEMAT_KOMEND_ORGANOW = 'tenant.*.commands.organ.>' as const;

/** Usługi odpowiadające na pytania `rpc.*` (`02` §5): tylko odczyt, brak odpowiedzi = `nieustalony`. */
export const RPC_USLUG = {
  fabrica: ['plan_wstecz'],
  provisio: ['dostepnosc'],
  receptor: ['agent_online'],
} as const;

export type UslugaRpc = keyof typeof RPC_USLUG;
export type MetodaRpc<U extends UslugaRpc = UslugaRpc> = (typeof RPC_USLUG)[U][number];

/** `tenant.{org}.rpc.{usluga}.{metoda}` (request-reply core, `02` §3). */
export function tematRpc<U extends UslugaRpc>(
  organizacja: string,
  usluga: U,
  metoda: MetodaRpc<U>
): string {
  wymagajOrganizacji(organizacja);
  if (!maKlucz(RPC_USLUG, usluga) || !(RPC_USLUG[usluga] as readonly string[]).includes(metoda)) {
    throw new Error(`RPC magistrali: „${usluga}.${metoda}” spoza słownika`);
  }
  return `tenant.${organizacja}.rpc.${usluga}.${metoda}`;
}

/**
 * Telemetria organu Mandatum (`02` §4.2, §5): ładunek surowy, BEZ koperty
 * (enkoder go pomija), strumień TELEMETRY 7 dni. Pozycja pojazdu to dana
 * osobowa kierowcy: brama przekazuje ją wyłącznie rolom dyspozytorskim
 * (PR 1.2a), nigdy sesjom urządzeń.
 */
export const MANDATUM_TELEMETRY = {
  vehiclePosition: (organizacja: string, pojazdId: string): string => {
    wymagajOrganizacji(organizacja);
    if (!CZLON_IDENTYFIKATORA_RE.test(pojazdId)) {
      throw new Error(
        `Telemetria magistrali: identyfikator pojazdu „${pojazdId}” nie jest jednym członem`
      );
    }
    return `tenant.${organizacja}.telemetry.vehicle.${pojazdId}.position`;
  },
  pickingVoice: (organizacja: string): string => {
    wymagajOrganizacji(organizacja);
    return `tenant.${organizacja}.telemetry.mandatum.picking_voice`;
  },
} as const;

/** Nagłówki koperty magistrali (`02` §4.1). */
export const NAGLOWKI_MAGISTRALI = {
  msgId: 'Nats-Msg-Id',
  agregat: 'Insolitum-Agregat',
  wersja: 'Insolitum-Wersja',
  traceparent: 'traceparent',
} as const;

/** Temat magistrali zamówień rozłożony na człony — wyłącznie tematy ze słownika. */
export type TematMagistrali =
  | { rodzaj: 'events'; organizacja: string; rodzina: RodzinaZdarzen; nazwa: NazwaZdarzenia }
  | { rodzaj: 'commands'; organizacja: string; organ: OrganKomend; akcja: AkcjaOrganu };

/**
 * Odwrotność budowniczych: konsument bierze organizację z TEMATU (`02` §2
 * pkt 4) i porównuje ją z kopertą (niezgodność → DLQ `izolacja`). Temat
 * spoza słownika, z organizacją w złym kształcie albo z inną liczbą członów
 * daje `null` — nigdy wyjątek, bo wejście jest z drutu.
 */
export function parsujTematMagistrali(temat: string): TematMagistrali | null {
  const czlony = temat.split('.');
  if (czlony[0] !== 'tenant' || !ORGANIZACJA_TEMATU_RE.test(czlony[1] ?? '')) return null;
  const organizacja = czlony[1] as string;
  if (czlony.length === 5 && czlony[2] === 'events') {
    const rodzina = czlony[3] as string;
    const nazwa = czlony[4] as string;
    if (!jestRodzinaZdarzen(rodzina) || !jestZdarzeniemRodziny(rodzina, nazwa)) return null;
    return { rodzaj: 'events', organizacja, rodzina, nazwa };
  }
  if (czlony.length === 6 && czlony[2] === 'commands' && czlony[3] === 'organ') {
    const organ = czlony[4] as string;
    const akcja = czlony[5] as string;
    if (!jestOrganKomend(organ) || !jestAkcjaOrganu(organ, akcja)) return null;
    return { rodzaj: 'commands', organizacja, organ, akcja };
  }
  return null;
}

// -----------------------------------------------------------------------------
// Konsumenci (`02` §6). Pola nazwane jak `DurableConsumerOptions`
// z `@insolitum/nats-client` (PR 1.1), czasy w MILISEKUNDACH; do
// `durableConsumer` podaje się `opcjeKonsumenta(k)`, nie obiekt ze słownika.
// -----------------------------------------------------------------------------

/** 30 s, 1, 2, 10, 30 min, 1 h — organy i konsumenci EVENTS rdzenia (`02` §6). */
const BACKOFF_ZLECEN_MS = [30_000, 60_000, 120_000, 600_000, 1_800_000, 3_600_000] as const;
/** 10 s, 30 s, 2, 10 min — integracje (limity dostawców nigdy przez `nak`, `02` §6). */
const BACKOFF_INTEGRACJI_MS = [10_000, 30_000, 120_000, 600_000] as const;

export interface KonsumentMagistrali {
  /** `durable_name` — ten sam człon wchodzi w temat advisory. */
  nazwa: string;
  strumien: typeof STRUMIEN_KOMEND_ORGANOW | 'EVENTS' | typeof STRUMIEN_ADVISORIES;
  /** Usługa, w której konsument żyje (`02` §6, kolumna „Usługa”). */
  usluga: string;
  filterSubject?: string;
  filterSubjects?: readonly string[];
  backoff?: readonly number[];
  /** `-1` = bez limitu doręczeń. */
  maxDeliver: number;
  maxAckPending?: number;
}

function konsumentKomend(
  organ: OrganKomend,
  nazwa: string,
  usluga: string,
  backoff: readonly number[],
  maxAckPending: number
): KonsumentMagistrali {
  return {
    nazwa,
    strumien: STRUMIEN_KOMEND_ORGANOW,
    usluga,
    filterSubject: filtrKomendOrganu(organ),
    backoff,
    maxDeliver: 8,
    maxAckPending,
  };
}

/** Jeden trwały konsument `ORGAN_COMMANDS` na organ, filtr rozłączny (WorkQueue nie przyjmie nakładających się). */
export const KONSUMENCI_KOMEND: Readonly<Record<OrganKomend, KonsumentMagistrali>> = {
  mandatum: konsumentKomend(
    'mandatum',
    'mandatum-core-zlecenia',
    'mandatum-core',
    BACKOFF_ZLECEN_MS,
    200
  ),
  packflow: konsumentKomend(
    'packflow',
    'packflow-core-zlecenia',
    'packflow-core',
    BACKOFF_ZLECEN_MS,
    200
  ),
  fabrica: konsumentKomend(
    'fabrica',
    'fabrica-core-zapotrzebowanie',
    'fabrica-core',
    BACKOFF_ZLECEN_MS,
    50
  ),
  receptor: konsumentKomend(
    'receptor',
    'receptor-core-zapotrzebowanie',
    'receptor-core',
    BACKOFF_ZLECEN_MS,
    50
  ),
  arkusz_produkcyjny: konsumentKomend(
    'arkusz_produkcyjny',
    'print-core-arkusz',
    'print-core',
    BACKOFF_ZLECEN_MS,
    50
  ),
  provisio: konsumentKomend(
    'provisio',
    'provisio-plan-komendy',
    'nexus-core',
    BACKOFF_ZLECEN_MS,
    50
  ),
  integracje: konsumentKomend(
    'integracje',
    'integracje-intencje',
    'integracje-core',
    BACKOFF_INTEGRACJI_MS,
    500
  ),
  kanal: konsumentKomend(
    'kanal',
    'integracje-kanal',
    'integracje-core',
    BACKOFF_INTEGRACJI_MS,
    500
  ),
};

/**
 * Czterej konsumenci EVENTS rdzenia — jedyni z EVENTS, których advisory
 * zbiera `ADVISORIES` (`02` §6). `filterSubjects` pełnymi tematami: `*` i `>`
 * wyłącznie jako cały człon.
 */
export const KONSUMENCI_ZDARZEN_RDZENIA = {
  provisioPlan: {
    nazwa: 'provisio-plan-zdarzenia',
    strumien: 'EVENTS',
    usluga: 'nexus-core',
    filterSubjects: [
      filtrZdarzenia('orders', 'production_demand_frozen'),
      filtrZdarzenia('orders', 'production_demand_status_changed'),
    ],
    backoff: BACKOFF_ZLECEN_MS,
    maxDeliver: 8,
    maxAckPending: 200,
  },
  miedzynajemcowy: {
    nazwa: 'orders-core-miedzynajemcowy',
    strumien: 'EVENTS',
    usluga: 'orders-core',
    // Dziewięć pełnych tematów FO + odrzucenie i anulowanie zamówienia
    // (handler bierze wyłącznie zamówienia z `partner_link_id`).
    filterSubjects: [
      ...RODZINY_ZDARZEN.orders.nazwy
        .filter((nazwa) => nazwa.startsWith('fulfillment_order_'))
        .map((nazwa) => filtrZdarzenia('orders', nazwa)),
      filtrZdarzenia('orders', 'order_rejected'),
      filtrZdarzenia('orders', 'order_cancelled'),
    ],
    backoff: BACKOFF_ZLECEN_MS,
    maxDeliver: 8,
    maxAckPending: 200,
  },
  poczta: {
    nazwa: 'poczta-zamowienia',
    strumien: 'EVENTS',
    usluga: 'neurosense-core',
    filterSubjects: [
      filtrZdarzenia('orders', 'promise_date_changed'),
      filtrZdarzenia('orders', 'fulfillment_order_failed'),
      filtrZdarzenia('orders', 'batch_recall_opened'),
      filtrZdarzenia('documents', 'document_rejected'),
      filtrRodziny('integracje'),
      filtrZdarzenia('mandatum', 'stop_problem'),
      filtrZdarzenia('mandatum', 'intake_overdue'),
      filtrZdarzenia('provisio', 'material_shortage_detected'),
      filtrZdarzenia('provisio', 'order_overdue'),
    ],
    backoff: BACKOFF_ZLECEN_MS,
    maxDeliver: 8,
    maxAckPending: 100,
  },
  mobilePush: {
    nazwa: 'mobile-push-zamowienia',
    strumien: 'EVENTS',
    usluga: 'nexus-core',
    filterSubjects: [
      filtrZdarzenia('orders', 'promise_date_changed'),
      filtrZdarzenia('orders', 'order_held'),
      filtrZdarzenia('orders', 'fulfillment_order_rejected'),
      filtrZdarzenia('orders', 'fulfillment_order_shorted'),
      filtrZdarzenia('orders', 'production_change_requested'),
      filtrZdarzenia('provisio', 'material_shortage_detected'),
    ],
    backoff: BACKOFF_ZLECEN_MS,
    maxDeliver: 8,
    maxAckPending: 100,
  },
} as const satisfies Record<string, KonsumentMagistrali>;

/** Konsument advisory (`02` §6, §8): bez limitu doręczeń, alert na `num_redelivered`. */
export const KONSUMENT_ADVISORY: KonsumentMagistrali = {
  nazwa: 'orders-core-advisory',
  strumien: STRUMIEN_ADVISORIES,
  usluga: 'orders-core',
  maxDeliver: -1,
};

/** Opcje `durableConsumer` (`@insolitum/nats-client`) — te same nazwy pól co `DurableConsumerOptions`. */
export interface OpcjeKonsumentaMagistrali {
  filterSubject?: string;
  filterSubjects?: string[];
  backoff?: number[];
  maxDeliver: number;
  maxAckPending?: number;
}

/**
 * Świeży obiekt opcji dla `durableConsumer` (tablice skopiowane: słownik jest
 * wspólny dla procesu i nie wolno go zmienić przez przypadek w jednej usłudze).
 */
export function opcjeKonsumenta(k: KonsumentMagistrali): OpcjeKonsumentaMagistrali {
  return {
    ...(k.filterSubject !== undefined ? { filterSubject: k.filterSubject } : {}),
    ...(k.filterSubjects !== undefined ? { filterSubjects: [...k.filterSubjects] } : {}),
    ...(k.backoff !== undefined ? { backoff: [...k.backoff] } : {}),
    maxDeliver: k.maxDeliver,
    ...(k.maxAckPending !== undefined ? { maxAckPending: k.maxAckPending } : {}),
  };
}

/** Rodzaje advisory zbierane w `ADVISORIES` — reszta (np. `MSG_ACKED`) nie ma czytelnika. */
const RODZAJE_ADVISORY = ['MAX_DELIVERIES', 'MSG_TERMINATED'] as const;

/**
 * Tematy strumienia `ADVISORIES` (`02` §6): wszyscy konsumenci
 * `ORGAN_COMMANDS` oraz WYŁĄCZNIE czterej konsumenci EVENTS rdzenia. Advisory
 * innych konsumentów konta zostają poza strumieniem — `nexus-encoder` kończy
 * `term()` każdą surową telemetrię (ok. 3 tys. odczytów na dobę, §1),
 * a `processTerm` wysyła advisory zawsze. Liczone z listy konsumentów, nie
 * wpisane drugi raz.
 */
export const TEMATY_ADVISORIES: readonly string[] = [
  ...RODZAJE_ADVISORY.map(
    (rodzaj) => `$JS.EVENT.ADVISORY.CONSUMER.${rodzaj}.${STRUMIEN_KOMEND_ORGANOW}.>`
  ),
  ...Object.values(KONSUMENCI_ZDARZEN_RDZENIA).flatMap((k) =>
    RODZAJE_ADVISORY.map((rodzaj) => `$JS.EVENT.ADVISORY.CONSUMER.${rodzaj}.EVENTS.${k.nazwa}`)
  ),
];

// -----------------------------------------------------------------------------
// `msg_id` (`02` §4.1): `{producent}:{agregat_typ}:{agregat_id}:v{wersja}:{nazwa}`,
// ponowienie komendy po terminie `:p{N}` (N = `bus_outbox.proba`). Nazwa
// w kluczu pozwala jednej zmianie dać zdarzenie i komendę
// (`fulfillment_order_created` + `fulfillment_request`) bez naruszenia
// `UNIQUE (msg_id)`. Ten sam napis składa SQL `bus_wstaw` (PR 2.1) — to
// jest wzorzec, z którym ma się zgadzać.
// -----------------------------------------------------------------------------

export interface CzesciMsgId {
  producent: ProducentOutboxu;
  /** Typ agregatu, np. `fulfillment_order` (człon nazwy). */
  agregatTyp: string;
  /** Identyfikator agregatu — bez dwukropka, małymi literami. */
  agregatId: string;
  /** Wersja agregatu po zmianie (liczba całkowita ≥ 0). */
  wersja: number;
  /** Nazwa zdarzenia rodziny producenta albo — dla `orders-core` — akcja komendy. */
  nazwa: string;
  /** Ponowienie komendy (`bus_outbox.proba`); 0 albo brak = bez `:p{N}`. */
  proba?: number;
}

/** Nazwy, które wolno wpisać w `msg_id` producenta: zdarzenia jego rodzin, a rdzeniowi także akcje komend. */
function nazwyProducenta(producent: string): ReadonlySet<string> {
  const nazwy = new Set<string>();
  for (const rodzina of Object.keys(RODZINY_ZDARZEN) as RodzinaZdarzen[]) {
    if (RODZINY_ZDARZEN[rodzina].producent === producent) {
      for (const n of RODZINY_ZDARZEN[rodzina].nazwy) nazwy.add(n);
    }
  }
  if (producent === PRODUCENT_KOMEND_ORGANOW) {
    for (const akcje of Object.values(KOMENDY_ORGANOW)) for (const a of akcje) nazwy.add(a);
  }
  return nazwy;
}

const AGREGAT_ID_RE = /^[a-z0-9][a-z0-9_-]*$/;
const liczbaNieujemna = (n: number): boolean => Number.isInteger(n) && n >= 0;

function bladMsgId(c: CzesciMsgId): string | null {
  const nazwy = nazwyProducenta(c.producent);
  if (nazwy.size === 0) return `producent „${c.producent}” spoza outboxu`;
  if (!CZLON_NAZWY_RE.test(c.agregatTyp)) return `typ agregatu „${c.agregatTyp}”`;
  if (!AGREGAT_ID_RE.test(c.agregatId)) return `identyfikator agregatu „${c.agregatId}”`;
  if (!liczbaNieujemna(c.wersja)) return `wersja ${c.wersja}`;
  if (c.proba !== undefined && !liczbaNieujemna(c.proba)) return `próba ${c.proba}`;
  if (!nazwy.has(c.nazwa)) {
    return `nazwa „${c.nazwa}” nie należy do rodzin producenta „${c.producent}”`;
  }
  return null;
}

export function budujMsgId(c: CzesciMsgId): string {
  const blad = bladMsgId(c);
  if (blad !== null) throw new Error(`msg_id magistrali: ${blad}`);
  const proba = c.proba !== undefined && c.proba > 0 ? `:p${c.proba}` : '';
  return `${c.producent}:${c.agregatTyp}:${c.agregatId}:v${c.wersja}:${c.nazwa}${proba}`;
}

/** Odwrotność {@link budujMsgId}; `null` dla napisu, którego `budujMsgId` by nie zbudował. */
export function parsujMsgId(msgId: string): CzesciMsgId | null {
  const czlony = msgId.split(':');
  if (czlony.length !== 5 && czlony.length !== 6) return null;
  const [producent, agregatTyp, agregatId, wersjaTekst, nazwa, probaTekst] = czlony as [
    string,
    string,
    string,
    string,
    string,
    string | undefined,
  ];
  const wersja = /^v(0|[1-9]\d*)$/.exec(wersjaTekst);
  if (wersja === null) return null;
  let proba: number | undefined;
  if (probaTekst !== undefined) {
    const p = /^p([1-9]\d*)$/.exec(probaTekst);
    if (p === null) return null;
    proba = Number(p[1]);
  }
  const czesci: CzesciMsgId = {
    producent: producent as ProducentOutboxu,
    agregatTyp,
    agregatId,
    wersja: Number(wersja[1]),
    nazwa,
    ...(proba !== undefined ? { proba } : {}),
  };
  return bladMsgId(czesci) === null ? czesci : null;
}
